import React, { useState, useMemo } from 'react';
import { Timer } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { NumberField } from '../../components/calculators/FormFields';
import UnitToggle from '../../components/calculators/UnitToggle';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculatePace, racePredictions } from '../../lib/calculatorUtils';

const PaceCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [distance, setDistance] = useState('');
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [seconds, setSeconds] = useState('');

  const result = useMemo(() => {
    const d = parseFloat(distance);
    const h = parseFloat(hours) || 0;
    const m = parseFloat(minutes) || 0;
    const s = parseFloat(seconds) || 0;
    if (!d || (h === 0 && m === 0 && s === 0)) return null;
    const distanceKm = unit === 'metric' ? d : d * 1.60934;
    const totalMin = h * 60 + m + s / 60;
    const p = calculatePace(distanceKm, totalMin);
    if (!p) return null;
    return { ...p, predictions: racePredictions(p.perKmMin) };
  }, [unit, distance, hours, minutes, seconds]);

  const inputs = (
    <div className="space-y-5" data-testid="pace-inputs">
      <div className="flex items-center justify-between">
        <h2 className="text-xl sm:text-2xl font-bold text-white">Run Details</h2>
        <UnitToggle value={unit} onChange={setUnit} options={[{ value: 'metric', label: 'km' }, { value: 'imperial', label: 'mi' }]} label="Distance unit" testIdPrefix="pace-unit" />
      </div>
      <p className="text-slate-400 text-sm">Enter your distance and time to compute pace and race predictions.</p>
      <NumberField id="pace-distance" label={`Distance (${unit === 'metric' ? 'km' : 'mi'})`} value={distance} onChange={setDistance} placeholder={unit === 'metric' ? 'e.g. 5' : 'e.g. 3.1'} suffix={unit === 'metric' ? 'km' : 'mi'} min={0.1} max={500} testId="pace-distance-input" required />
      <div className="grid grid-cols-3 gap-2">
        <NumberField id="pace-h" label="Hours" value={hours} onChange={setHours} placeholder="0" min={0} max={24} testId="pace-hours-input" />
        <NumberField id="pace-m" label="Minutes" value={minutes} onChange={setMinutes} placeholder="0" min={0} max={59} testId="pace-minutes-input" />
        <NumberField id="pace-s" label="Seconds" value={seconds} onChange={setSeconds} placeholder="0" min={0} max={59} testId="pace-seconds-input" />
      </div>
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label={`Pace per ${unit === 'metric' ? 'km' : 'mile'}`} value={unit === 'metric' ? result.perKm : result.perMile} unit={`min/${unit === 'metric' ? 'km' : 'mi'}`} accent="from-emerald-500 to-cyan-500" testId="pace-result-value" />
      <div className="mt-8 space-y-1">
        <StatRow label="Pace per km" value={result.perKm} unit="min/km" testId="pace-per-km" />
        <StatRow label="Pace per mile" value={result.perMile} unit="min/mi" testId="pace-per-mile" />
        <StatRow label="Speed" value={result.speedKph} unit="km/h" testId="pace-kph" />
        <StatRow label="Speed" value={result.speedMph} unit="mph" testId="pace-mph" />
      </div>
      <div className="mt-6 bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
        <p className="text-slate-300 text-sm font-semibold mb-3">Race time predictions (at this pace)</p>
        <div className="grid grid-cols-2 gap-2 text-center text-xs">
          {result.predictions.map((p) => (
            <div key={p.name} className="bg-slate-900/40 border border-slate-700/40 rounded-lg p-2" data-testid={`pace-race-${p.name.replace(/\s/g, '-').toLowerCase()}`}>
              <p className="text-slate-500">{p.name}</p>
              <p className="text-white font-bold">{p.time}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  ) : (
    <EmptyResult message="Enter distance and time to compute pace and race predictions." />
  );

  return (
    <CalculatorLayout
      slug="pace" title="Pace Calculator"
      subtitle="Calculate your running or walking pace, speed and race time predictions for 5K, 10K, half and full marathon."
      metaDescription="Pace Calculator for running pace per km/mile, speed and race time predictions."
      icon={Timer}
      accentGradient="from-emerald-500 to-cyan-500" accentColor="text-emerald-400"
      badgeBg="bg-emerald-500/10" badgeBorder="border-emerald-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p><strong className="text-slate-300">Pace:</strong> Pace = Time ÷ Distance</p>
        <p><strong className="text-slate-300">Speed:</strong> Speed = Distance ÷ Time</p>
      </>}
    />
  );
};

export default PaceCalculatorPage;
